import { useForm } from 'react-hook-form'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { Building2, Camera } from 'lucide-react'
import { useActiveOrgProfile } from '@/hooks/useActiveOrgProfile'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { useUpdateOrgProfile } from '@/services/profile'
import { cn } from '@/lib/utils'
import { US_STATES } from './constants'
import {
  FileUploadSchema,
  OrgProfileFormValues,
  ValidationMessages,
} from './types'
import { Separator } from '@/components/ui/separator'
import { UpdateOrgProfileRequest } from '@/services/inference.gen'
import toast from 'react-hot-toast'

const stateEnum = z.enum(
  US_STATES.map((state) => state.value) as [string, ...string[]]
)

const OrgProfileFormSchema = z.object({
  orgName: z
    .string()
    .min(1, ValidationMessages.required('Organization name'))
    .max(50, ValidationMessages.maxLength('Organization name', 50)),
  department: z
    .string()
    .max(20, ValidationMessages.maxLength('Department name', 20)),
  address1: z
    .string()
    .min(1, ValidationMessages.required('Address'))
    .max(50, ValidationMessages.maxLength('Address', 50)),
  address2: z.string().max(50, ValidationMessages.maxLength('Address', 50)),
  city: z
    .string()
    .min(1, ValidationMessages.required('City'))
    .max(20, ValidationMessages.maxLength('City name', 20)),
  state: stateEnum,
  zip: z
    .string()
    .min(1, ValidationMessages.required('ZIP code'))
    .max(10, ValidationMessages.maxLength('ZIP code', 10)),
  logo: FileUploadSchema,
})

export const OrgProfileForm = () => {
  const { data: orgData } = useActiveOrgProfile()
  const { mutateAsync, isPending } = useUpdateOrgProfile()

  const form = useForm<OrgProfileFormValues>({
    resolver: zodResolver(OrgProfileFormSchema),
    defaultValues: {
      orgName: orgData?.org?.name || '',
      department: orgData?.org?.profile_metadata?.department_name || '',
      address1: orgData?.org?.profile_metadata?.address1 || '',
      address2: orgData?.org?.profile_metadata?.address2 || '',
      city: orgData?.org?.profile_metadata?.city || '',
      state: orgData?.org?.profile_metadata?.state || '',
      zip: orgData?.org?.profile_metadata?.zip_code || '',
    },
    mode: 'onChange',
  })

  const logoPreviewUrl = (() => {
    const logoFile = form.watch('logo')
    if (logoFile instanceof File) {
      return URL.createObjectURL(logoFile)
    }
    return orgData?.org?.profile_metadata?.avatar_preview_url
  })()

  async function onSubmit(data: OrgProfileFormValues) {
    const payload: UpdateOrgProfileRequest = {
      org_name: data.orgName,
      department_name: data.department,
      address1: data.address1,
      address2: data.address2,
      city: data.city,
      state: data.state,
      zip_code: data.zip,
      country: '',
    }

    const reader = new FileReader()

    try {
      reader.onload = async () => {
        const base64File = (reader.result as string)?.split(',')[1] // remove "data:<type>;base64," prefix
        if (data.logo) {
          payload.avatar = {
            file_content: base64File,
            file_name: data.logo.name || '',
            file_type: data.logo.type || '',
          }
        }

        await mutateAsync(payload)
      }

      if (data.logo) {
        reader.readAsDataURL(data.logo)
      } else {
        await mutateAsync(payload)
      }
    } catch (error) {
      console.error(error)
      toast.error(JSON.stringify(error))
    }
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="flex flex-col gap-6 w-full max-w-3xl"
      >
        <Card className="border-primary/20 shadow-sm">
          <CardHeader className="pb-4">
            <CardTitle className="text-xl font-semibold text-primary">
              Organization Information
            </CardTitle>
          </CardHeader>

          <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="flex flex-col items-center justify-start space-y-4">
              <h3 className="text-sm font-medium text-muted-foreground self-center">
                Organization Logo
              </h3>

              <FormField
                control={form.control}
                name="logo"
                render={({ field }) => (
                  <FormItem className="w-full flex flex-col items-center">
                    <FormControl>
                      <div className="relative group">
                        <div className="w-40 h-40 rounded-full overflow-hidden border-2 border-primary/30 flex items-center justify-center bg-muted/30">
                          {logoPreviewUrl ? (
                            <img
                              src={logoPreviewUrl}
                              alt="Organization Logo"
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <Building2 className="h-16 w-16 text-muted-foreground/50" />
                          )}
                        </div>
                        <div className="absolute inset-0 flex items-center justify-center bg-foreground/40 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                          <Camera className="h-8 w-8 text-background" />
                        </div>
                        <input
                          type="file"
                          accept="image/*"
                          id="logo-upload"
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                          onChange={(e) => {
                            const file = e.target.files?.[0]
                            if (!file) return

                            if (!file.type.startsWith('image/')) {
                              form.setError('logo', {
                                message: ValidationMessages.fileType('image'),
                              })
                              return
                            }

                            if (file.size > 500 * 1024) {
                              form.setError('logo', {
                                message: ValidationMessages.fileSize('500KB'),
                              })
                              return
                            }

                            field.onChange(file)
                          }}
                        />
                      </div>
                    </FormControl>
                    <p className="text-xs text-center text-muted-foreground max-w-[200px] mt-2">
                      Click to upload (max 500KB)
                    </p>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="md:col-span-2 space-y-6">
              <div className="space-y-2">
                <h3 className="text-sm font-medium text-muted-foreground">
                  Organization Details
                </h3>
                <Separator className="mb-4" />
                <div className="space-y-4">
                  <FormField
                    control={form.control}
                    name="orgName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Organization Name</FormLabel>
                        <FormControl>
                          <Input
                            type="text"
                            className={cn('w-full', 'focus:ring-primary/20')}
                            placeholder="Enter organization name"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="department"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Department (Optional)</FormLabel>
                        <FormControl>
                          <Input
                            type="text"
                            className={cn('w-full', 'focus:ring-primary/20')}
                            placeholder="Enter department name"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <h3 className="text-sm font-medium text-muted-foreground">
                  Location Information
                </h3>
                <Separator className="mb-4" />
                <div className="space-y-4">
                  <FormField
                    control={form.control}
                    name="address1"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Address Line 1</FormLabel>
                        <FormControl>
                          <Input
                            type="text"
                            className={cn('w-full', 'focus:ring-primary/20')}
                            placeholder="Enter street address"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="address2"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Address Line 2 (Optional)</FormLabel>
                        <FormControl>
                          <Input
                            type="text"
                            className={cn('w-full', 'focus:ring-primary/20')}
                            placeholder="Enter suite, apartment, etc."
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <FormField
                      control={form.control}
                      name="city"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>City</FormLabel>
                          <FormControl>
                            <Input
                              type="text"
                              className={cn('w-full', 'focus:ring-primary/20')}
                              placeholder="Enter city"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="state"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>State</FormLabel>
                          <Select
                            onValueChange={field.onChange}
                            defaultValue={field.value}
                          >
                            <FormControl>
                              <SelectTrigger className="focus:ring-primary/20">
                                <SelectValue placeholder="Select state" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {US_STATES.map((state) => (
                                <SelectItem
                                  key={state.value}
                                  value={state.value}
                                >
                                  {state.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="zip"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>ZIP Code</FormLabel>
                          <FormControl>
                            <Input
                              type="text"
                              className={cn('w-full', 'focus:ring-primary/20')}
                              placeholder="Enter ZIP code"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end mt-4">
          <Button
            type="submit"
            disabled={
              isPending || Object.keys(form.formState.errors).length > 0
            }
            className="px-6"
          >
            {isPending ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </form>
    </Form>
  )
}
